<template>
  <b-sidebar
    id="sidebar-create-apply-document"
    sidebar-class="sidebar-lg"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ applyDocument.id ? $t('Update') : $t('Create') }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="clear(hide)"
        />

      </div>
      <!-- Body -->
      <b-form
        v-if="activeQuarter.id || canUpdateByInno(applyDocument)"
        class="p-2"
        @submit.prevent
      >
        <validation-observer ref="applyDocumentCreate">
          <!-- Dc Type -->
          <b-form-group
            :label="$t('Dc Type')"
            label-for="dc_type"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Dc Type')"
              rules="required"
            >
              <v-select
                id="dc_type"
                v-model="applyDocument.dc_type"
                :reduce="(option) => option.id"
                :options="canUpdateByInno(applyDocument) ? dcTypes : getDcTypesForQuarter()"
                :get-option-label="(option) => $langName(option.name)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Edu Lang -->
          <b-form-group
            :label="$t('Education Language')"
            label-for="edu_lang"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Education Language')"
              rules="required"
            >
              <v-select
                id="edu_lang"
                v-model="applyDocument.edu_lang"
                :reduce="(option) => option.id"
                :options="eduLangTypes"
                :get-option-label="(option) => $langName(option.name)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Organizations -->
          <b-form-group
            :label="$t('Organizations')"
            label-for="organization"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Organization')"
              rules="required"
            >
              <v-select
                id="organization"
                v-model="applyDocument.organization"
                :reduce="(option) => option.id"
                :options="organizations"
                :get-option-label="(option) => $langName(option.name)"
                @input="applyDocument.direction = null; loadDirections($event)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Status-->
          <b-form-group
            v-if="canUpdateByInno(applyDocument)"
            :label="$t('Status')"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Status')"
              rules="required"
            >
              <v-select
                v-model="applyDocument.status"
                :reduce="(option) => option.id"
                :options="applyDocumentStatuses"
                :get-option-label="(option) => $langName(option.name)"
                :placeholder="$t('All')"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="canUpdateByInno(applyDocument) && (applyDocument.status === applyDocumentStatusRejected)"
            :label="$t('Rejected info')"
            label-for="rejected-info"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Rejected info')"
              rules="required"
            >
              <b-form-textarea
                id="rejected-info"
                v-model="applyDocument.rejected_info"
                trim
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <div
            v-if="canUpdateByInno(applyDocument)"
          >
            <!-- Scientific work name -->
            <b-form-group
              :label="$t('Scientific work name')"
              label-for="scientific_work_name"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Scientific work name')"
              >
                <b-form-input
                  id="scientific_work_name"
                  v-model="applyDocument.scientific_work_name"
                  :state="errors.length > 0 ? false:null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Quarter -->
            <validation-provider
              #default="{ errors }"
              :name="$t('Quarter')"
              rules="required"
            >
              <b-form-group
                :label="$t('Quarter')"
                label-for="region"
              >
                <v-select
                  id="country"
                  v-model="applyDocument.quarter"
                  :reduce="(option) => option.id"
                  :options="quarters"
                  :get-option-label="(option) => $t('{year}-year, {quarter}-quarter', { year: option.period.admission_year, quarter: option.quarter})"
                />
              </b-form-group>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>

            <validation-provider
              v-for="monitoring of ['monitoring_1', 'monitoring_2', 'monitoring_3']"
              #default="{ errors }"
              :key="monitoring"
              :name="$t(monitoring)"
            >
              <b-form-group
                :label="$t(monitoring)"
                :label-for="monitoring"
              >
                <v-select
                  :id="monitoring"
                  v-model="applyDocument[monitoring]"
                  :reduce="(option) => option.id"
                  :options="checkMonitoringStatus"
                  :get-option-label="(option) => $langName(option)"
                />
              </b-form-group>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
            <b-form-group
              :label="$t('Course')"
              label-for="course"
            >
              <v-select
                id="course"
                v-model="applyDocument.course"
                :options="[1,2,3]"
              />
            </b-form-group>
            <validation-provider
              v-for="(monitoring, index) of applyDocument.monitoring_reports"
              #default="{ errors }"
              :key="'monitoringReports_' + index"
            >
              <b-form-group
                :label="$t('Monitoring Report') + '(' + $t('Course') + '-' + monitoring.course +')'"
                :label-for="'monitoringReports_' + index"
              >
                <v-select
                  :id="'monitoringReports_' + index"
                  v-model="monitoring.status"
                  :reduce="(option) => option.id"
                  :options="monitoringReportStatuses"
                  :get-option-label="(option) => $langName(option.name)"
                />
              </b-form-group>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </div>

          <!-- Directions -->
          <b-form-group
            :label="$t('Directions')"
            label-for="direction"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Directions')"
              rules="required"
            >
              <v-select
                id="direction"
                v-model="applyDocument.direction"
                :reduce="(option) => option.direction.id"
                :options="directions"
                :get-option-label="(option) => option.direction ? option.direction.code + ' - ' + $langName(option.direction.name): null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('Benefit Type')"
            label-for="benefit_type"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Benefit Type')"
              rules="required"
            >
              <v-select
                id="benefit_type"
                v-model="applyDocument.benefit_type"
                :reduce="(option) => option.id"
                :options="typeBenefitsList"
                :get-option-label="(option) => $langName(option)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            v-if="showDisabilitySubtypes"
            :label="$t('Disability Type')"
            label-for="disability_type"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Disability Type')"
              rules="required"
            >
              <v-select
                id="disability_type"
                v-model="applyDocument.disability_type"
                :reduce="(option) => option.id"
                :options="disabilitySubtypes"
                :get-option-label="(option) => $langName(option)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <!-- Directions -->
          <b-form-group
            :label="$t('Certificate')"
            label-for="certificate"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Certificate')"
            >
              <v-select
                id="direction"
                v-model="applyDocument.certificate_lang"
                :reduce="(option) => option.id"
                :options="certificates"
                :get-option-label="(option) => $langName(option)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            :label="$t('Certificate Type')"
            label-for="certificate_type"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Certificate Type')"
            >
              <v-select
                id="direction"
                v-model="applyDocument.certificate_type"
                :reduce="(option) => option.id"
                :options="certificateTypes"
                :get-option-label="(option) => option.name"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('Certificate Degree')"
            label-for="certificate_degree"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Certificate Degree')"
            >
              <v-select
                id="direction"
                v-model="applyDocument.certificate_degree"
                :reduce="(option) => option.id"
                :options="degreeList"
                :get-option-label="(option) => option.name"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

        </validation-observer>
        <!-- Form Actions -->
        <div class="d-flex mt-2">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-2"
            type="submit"
            @click="submit(hide)"
          >
            {{ applyDocument.id ? $t('Update') : $t('Create') }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="clear(hide)"
          >
            {{ $t('Cancel') }}
          </b-button>
        </div>
      </b-form>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BButton, BFormInput, BFormTextarea,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver, localize } from 'vee-validate'
import { onUnmounted } from '@vue/composition-api'
import { required } from '@/libs/validations/validations'
import permission from './permission'
import store from '@/store'
import moduleMonitoringReport from '@/store/monitoring-report'
import { CertificateDegreeList } from '@/classes/CertificateDegrees'
import { TypeBenefits, TypeBenefitsList } from '@/classes/TypeBenefits'

export default {
  components: {
    BFormTextarea,
    BFormInput,
    ValidationProvider,
    ValidationObserver,
    BSidebar,
    BForm,
    BFormGroup,
    BButton,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      required,
    }
  },
  computed: {
    applyDocument: {
      get() {
        return this.$store.state.applyDocument.applyDocument
      },
      set(data) {
        this.$store.commit('applyDocument/UPDATE', data)
      },
    },
    dcTypes() {
      return this.$store.state.staticModule.dcTypes
    },
    eduLangTypes() {
      return this.$store.state.staticModule.eduLangTypes
    },
    organizations() {
      return this.$store.state.applyDocument.organizations
    },
    directions() {
      return this.$store.state.applyDocument.directions
    },
    activeQuarter() {
      return this.$store.state.staticModule.activeQuarter
    },
    applyDocumentStatuses() {
      return this.$store.state.staticModule.applyDocumentStatuses
    },
    quarters() {
      return this.$store.state.staticModule.quotes
    },
    checkMonitoringStatus() {
      return this.$store.state.staticModule.checkMonitoringAllStatus
    },
    monitoringReportStatuses() {
      return this.$store.state.monitoringReport.statuses
    },
    certificates() {
      return this.$store.state.staticModule.certificateList
    },
    certificateTypes() {
      return this.applyDocument.certificate_lang ? this.$store.state.staticModule.certificateTypes.filter(type => type.certificate_lang === this.applyDocument.certificate_lang) : []
    },
    showDisabilitySubtypes() {
      return this.applyDocument.benefit_type === TypeBenefits.DISABILITY_CERTIFICATE
    },
    disabilitySubtypes() {
      const disability = this.typeBenefitsList.find(b => b.id === 200)
      return disability?.subtypes || []
    },
  },
  mounted() {
    localize(this.$i18n.locale)
    this.$store.dispatch('staticModule/certificates')
    this.$store.dispatch('staticModule/certificateTypes')
    this.$store.dispatch('staticModule/dcType')
    this.$store.dispatch('staticModule/eduLangTypes')
    this.$store.dispatch('applyDocument/organizations')
  },
  methods: {
    loadDirections() {
      if (this.applyDocument.organization) {
        this.$store.dispatch('applyDocument/directions', { org: this.applyDocument.organization, limit: 9999 })
      }
    },
    getDcTypesForQuarter() {
      return this.$store.getters['staticModule/getDcTypeForQuarter'](this.activeQuarter.quarter)
    },
    clear(hide) {
      this.$store.commit('applyDocument/FRESH')
      this.$refs.applyDocumentCreate.reset()
      hide()
    },
    submit(hide) {
      this.$refs.applyDocumentCreate.validate().then(success => {
        if (success) {
          this.$createSwal(this, this.applyDocument, `applyDocument/${this.applyDocument.id ? 'update' : 'create'}`, () => {
            this.$emit('refetchData')
            this.$store.commit('applyDocument/FRESH')
          })
          hide()
        }
      })
    },
  },
  setup() {
    const MODULE_MONITORING_REPORT = 'monitoringReport'
    const { applyDocumentStatusRejected } = store.state.staticModule
    const degreeList = CertificateDegreeList
    const typeBenefitsList = TypeBenefitsList
    // Register module
    if (!store.hasModule(MODULE_MONITORING_REPORT)) store.registerModule(MODULE_MONITORING_REPORT, moduleMonitoringReport)

    onUnmounted(() => {
      if (store.hasModule(MODULE_MONITORING_REPORT)) store.unregisterModule(MODULE_MONITORING_REPORT, moduleMonitoringReport)
    })
    return {
      degreeList,
      ...permission(),
      typeBenefitsList,
      applyDocumentStatusRejected,
    }
  },
}
</script>
